import { DataSource, SelectionChange, SelectionModel } from '@angular/cdk/collections';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { MatSort, Sort } from '@angular/material/sort';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { sortBy } from './sort-by';

/**
 * `DataSource` to be used with `CoreTable` and `MatTable`.
 *
 * Supports advanced filtering, sorting, pagination and virtual scroll.
 */
export class CoreTableDataSource<T> extends DataSource<T> {
  /**
   * A stream of the filtered arrays of data
   */
  readonly filteredData: Observable<T[]>;

  private readonly filteredDataSubject = new BehaviorSubject<T[]>([]);
  private readonly selection = new SelectionModel<T>(true, []);
  private readonly visibleData: Observable<T[]>;
  private privateData: T[];

  constructor(
    initialData: T[],
    {
      sort,
      viewport,
    }: {
      sort?: MatSort;
      viewport?: CdkVirtualScrollViewport;
    } = {},
  ) {
    super();
    this.privateData = initialData;
    this.filteredData = this.filteredDataSubject.asObservable();
    this.filteredDataSubject.next(initialData);

    const ordered =
      sort === undefined
        ? this.filteredDataSubject
        : combineLatest([
            this.filteredDataSubject,
            sort.sortChange.pipe(startWith({} as Sort)),
          ]).pipe(
            map(([data, { active, direction }]) =>
              !active || !direction
                ? data
                : sortBy((t) => getProperty(t, active), {
                    reverse: direction === 'desc',
                  })(data),
            ),
          );

    const sliced =
      viewport === undefined
        ? ordered
        : combineLatest([ordered, viewport.renderedRangeStream]).pipe(
            map(([data, { start, end }]) =>
              start === undefined || end === undefined ? data : data.slice(start, end),
            ),
          );
    this.visibleData = sliced.pipe(shareReplay(1)) as Observable<T[]>;
  }

  /**
   * The full array of data
   */
  get allData(): T[] {
    return this.privateData.slice();
  }

  set allData(data: T[]) {
    this.privateData = data;
    this.filteredDataSubject.next(data);
  }

  /**
   * The array of visible data (being rendered)
   */
  get data(): T[] {
    let data: T[];
    this.visibleData.subscribe((d) => (data = d)).unsubscribe();

    return data;
  }

  /**
   * The array of currently selected items.
   */
  get selected(): T[] {
    return this.selection.selected;
  }

  /**
   * Whether all visible items are selected.
   */
  get selectedAll(): boolean {
    return this.selection.hasValue() && this.selected.length === this.data.length;
  }

  /**
   * A stream of changes made on selections.
   */
  get selectionChanged(): Observable<SelectionChange<T>> {
    return this.selection.changed;
  }

  /**
   * this custom function is added to update the selection if there is user interaction
   * while POS Items are still loading. Current POS Items are @4K and taking one minute to load full list.
   * this.selection is not being updated correctly with toggle() if there is user interaction during load.
   * This function is being used to update the selection based on the selected items
   * filtered from data source.selected
   */

  updateSelection(items: T[]): void {
    this.selection.clear();
    this.selection.select(...items);
  }

  /**
   * Clears all of the selected items.
   */
  clearSelection(): void {
    this.selection.clear();
  }

  /**
   * Whether an item is selected.
   */
  isSelected(item: T): boolean {
    return this.selection.isSelected(item);
  }

  /**
   * Selects one or more items.
   */
  select(...items: T[]): void {
    this.selection.select(...items);
  }

  /**
   * Toggles an item between selected and deselected.
   */
  toggle(item: T): void {
    this.selection.toggle(item);
  }

  /**
   * Toggles the selection of all visible items.
   *
   * If all are selected, deselects all.
   *
   * If some or none are selected, selects all.
   */
  toggleAll(): void {
    this.selectedAll ? this.selection.clear() : this.selection.select(...this.data);
  }

  connect() {
    return this.visibleData;
  }

  disconnect() {
    // no-op
  }
}

function getProperty<T>(item: T, propertyPath: string): string {
  return propertyPath.split('.').reduce((obj, prop) => obj && obj[prop], item);
}
